import React from 'react';
import strip from 'strip';
import ansiToHTML from 'ansi-to-html';
import normUrl from 'normalize-url';
import Markdown from 'markdown-to-jsx';

export function stripTags(str) {
  return strip(str);
}

/**
 * @param {HTMLDivElement} el
 * @returns {void}
 */
function removeStyleRecursively(el) {
  if (el.nodeType !== 1) {
    return;
  }

  el.removeAttribute('style');

  for (let i = 0; i < el.children.length; i++) {
    removeStyleRecursively(el.children[i]);
  }
}

export function removeStyles(str) {
  const div = document.createElement('div');
  div.innerHTML = str;

  removeStyleRecursively(div);

  return div.innerHTML;
}

/**
 * Expand a string template with object values
 *
 * This was copied from the API code
 *
 * @param {string} strTemp
 * @param {object} values
 * @param {object} [params]
 * @param {boolean} [params.unset] remove matched keys from from values
 * @param {boolean} [params.typecast] use original type if whole string matches a single value
 * @returns {string | null}
 */
export function expandString(
  strTemp,
  values,
  { unset = false, typecast = false } = {},
) {
  if (typeof strTemp !== 'string' || !strTemp) {
    return null;
  }
  let shouldAbort = false;
  let singleValue = null;
  let singleField = null;

  try {
    // Replace occurrences of {field} with values[field]
    const strExpanded = strTemp.replace(/\{.+?\}/g, (field) => {
      singleField = field;
      // Stripe curly braces
      field = field.substring(1, field.length - 1);
      let value;
      let defaultValue;
      // {field|default} values
      if (field.includes('|')) {
        const splitParts = field.split('|');
        field = splitParts[0].trim();
        defaultValue = splitParts[1].trim();
      }
      // {field.nested} values
      if (field.includes('.')) {
        const splitParts = field.split('.');
        field = splitParts.shift().trim();
        let nestedValues = values[field];
        while (splitParts.length > 0) {
          const nestedField = splitParts.shift().trim();
          if (splitParts.length > 0) {
            nestedValues = nestedValues ? nestedValues[nestedField] : {};
          } else {
            value = nestedValues ? nestedValues[nestedField] : null;
          }
        }
      } else {
        value = values[field];
      }
      // If field value not defined
      if (value === '' || value === null || value === undefined) {
        if (defaultValue !== undefined) {
          singleValue = String(defaultValue);
          return singleValue;
        }
        // Or abort expand
        shouldAbort = true;
        return;
      }
      // Unset value if it was expanded into string
      if (unset) {
        delete values[field];
      }
      singleValue = value;
      return value;
    });

    if (typecast && singleField === strTemp) {
      return singleValue;
    }

    return shouldAbort ? null : strExpanded;
  } catch (err) {
    console.error(err);
  }

  return strTemp;
}

/**
 * @param {string} str
 * @param {object} [options]
 * @param {boolean} [options.inline] Render inside a line of text, without
 *   paragraphs or lists
 * @returns {React.ReactNode}
 */
export function renderMarkdown(str, { inline = false } = {}) {
  if (typeof str !== 'string') {
    return null;
  }
  return (
    <Markdown
      options={{
        // The text comes from apps, themes and the assistant: HTML in it
        // would run in the dashboard
        disableParsingRawHTML: true,
        forceInline: inline,
        overrides: {
          a: {
            props: {
              target: '_blank',
              rel: 'noopener noreferrer',
              style: {
                color: '#175cd3',
              },
            },
          },
        },
      }}
    >
      {str}
    </Markdown>
  );
}

export function renderLogs(str, timestamps = false) {
  const convertAnsi = new ansiToHTML();
  return String(str || '')
    .split('\n')
    .map((rawLine) => {
      let line = rawLine;
      if (timestamps) {
        const ts = line.match(/^([0-9][^Z]+Z)/);
        if (ts && ts[1]) {
          const date = ts[1].replace('T', ' ').replace(/\.[0-9]+Z$/, '');
          line = line.replace(
            /^[0-9][^Z]+Z/,
            `<span class="date">${date}&emsp;</span>`,
          );
        }
      }
      return convertAnsi.toHtml(line) + '<br />';
    });
}

export function normalizeUrl(url, options = {}) {
  let result = normUrl(url, {
    defaultProtocol: 'https:',
    forceHttps: true,
    ...options,
  });
  // Correct for when `forceHttps` and `defaultProtocol` aren't working (??)
  if (options?.forceHttps !== false) {
    result = result.replace('http:', 'https:');
  }
  return result;
}

export function displayUrl(url) {
  return String(url)
    .trim()
    .replace(/^http:\/\//, '')
    .replace(/^https:\/\//, '')
    .replace(/^www\./, '');
}

/**
 * Splits long message into multiple lines
 * tries to separate using maxTextLineSeparator if possible to create user friendly lines.
 * returns array with parts
 *
 * @param {string} message string to split
 * @param {number} maxTextLineWidth maximum text line width
 * @param {string} maxTextLineSeparator try to separate on this places if possible
 * @param {number} maxDiff the interval to find separator from the proposed position
 * @return {string[]} array with parts
 */
export function separateStringOnParts(
  message,
  maxTextLineWidth,
  maxTextLineSeparator = ' ',
  maxDiff = 20,
) {
  const parts = [];
  let ind = 0;
  while (ind < message.length) {
    const nextPos = ind + maxTextLineWidth;
    let part = '';
    if (nextPos >= message.length) {
      // last part
      part = message.substring(ind, message.length);
      ind = message.length;
    } else {
      // find separator in range [0...maxDiff)
      let pos = nextPos;
      while (pos < message.length && pos < nextPos + maxDiff) {
        if (
          message.substring(pos, pos + maxTextLineSeparator.length) ===
          maxTextLineSeparator
        ) {
          // include separator to the previous part
          part = message.substring(ind, pos + maxTextLineSeparator.length);
          ind = pos + maxTextLineSeparator.length;
          break;
        }

        pos += 1;
      }

      // no separators near position - split on exact place
      if (part === '') {
        part = message.substring(ind, nextPos);
        ind = nextPos;
      }
    }

    part = part.trim();
    parts.push(part);
  }

  return parts;
}

/**
 * Splits long message into multiple lines
 * tries to separate using maxTextLineSeparator if possible to create user friendly lines.
 * returns prepared JSX.Element
 *
 * @param {string} message string to split
 * @param {number} maxTextLineWidth maximum text line width
 * @param {string} maxTextLineSeparator try to separate on this places if possible
 * @param {number} maxDiff the interval to find separator from the proposed position
 * @return {JSX.Element} prepared element with string parts and <br /> between them
 */
export function separateLongMessage(
  message,
  maxTextLineWidth,
  maxTextLineSeparator,
  maxDiff = 20,
) {
  const parts = separateStringOnParts(
    message,
    maxTextLineWidth,
    maxTextLineSeparator,
    maxDiff,
  );
  const result = parts.reduce((acc, value, index) => {
    if (!value) {
      return acc;
    }

    // separate parts with <br />
    if (acc.length > 0) {
      acc.push(<br key={index} />);
    }

    acc.push(value);
    return acc;
  }, []);

  return result;
}
